import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import {
  GCS_MEDIA_TYPE_PRESETS,
  isGcsFileAllowed,
  parseAllowedMediaTypes,
  type TContentBlockBase,
  type TGcsUploadRow,
} from '@onesource/schemas';
import {
  useCompleteBlock,
  useGetGcsDownloadUrl,
  useGetMyGcsUploadsByBlock,
  useNotifyGcsUpload,
  useStartGcsUpload,
} from '@onesource/services';
import DOMPurify from 'dompurify';
import { DragEvent, FC, useRef, useState } from 'react';
import {
  MdAccessTime,
  MdClose,
  MdCloudDownload,
  MdCloudUpload,
  MdDescription,
  MdImage,
  MdPlayArrow,
  MdVideoLibrary,
  MdVideoSettings,
} from 'react-icons/md';

interface GcsUploadPageProps {
  block: TContentBlockBase;
  lessonId: string;
}

type LayoutMetadata = {
  deadlineLabel?: string;
  sidebar?: { title?: string; html?: string; ctaLabel?: string; ctaUrl?: string };
  footer?: { html?: string; signature?: string };
};

// Error message used to flag a user-initiated upload cancellation.
const UPLOAD_CANCELLED = 'UPLOAD_CANCELLED';

const SERIF_FONT_OVERRIDE = {
  '&, & *': {
    fontFamily: 'Georgia, "Times New Roman", serif !important',
  },
} as const;

const isImage = (name: string) => /\.(png|jpe?g|gif|webp|svg|avif)$/i.test(name);
const isVideo = (name: string) => /\.(mp4|webm|mov|m4v|ogg|avi)$/i.test(name);
const isPdf = (name: string) => /\.pdf$/i.test(name);

const FileIcon = ({ name }: { name: string }) => {
  if (isImage(name)) {
    return <MdImage size={18} />;
  }
  if (isVideo(name)) {
    return <MdVideoLibrary size={18} />;
  }
  if (isPdf(name)) {
    return <MdDescription size={18} />;
  }
  return <MdCloudUpload size={18} />;
};

const formatRowDate = (iso: string | Date): string => {
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  const time = d.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZoneName: 'short',
  });
  return `${dd}/${mm}/${yy} · ${time}`;
};

const parseLayout = (metadata: unknown): LayoutMetadata => {
  if (!metadata || typeof metadata !== 'object') {
    return {};
  }
  const md = metadata as Record<string, unknown>;
  const sidebar = md.sidebar && typeof md.sidebar === 'object' ? (md.sidebar as Record<string, unknown>) : undefined;
  const footer = md.footer && typeof md.footer === 'object' ? (md.footer as Record<string, unknown>) : undefined;
  return {
    deadlineLabel: typeof md.deadlineLabel === 'string' ? md.deadlineLabel : undefined,
    sidebar: sidebar
      ? {
          title: typeof sidebar.title === 'string' ? sidebar.title : undefined,
          html: typeof sidebar.html === 'string' ? sidebar.html : undefined,
          ctaLabel: typeof sidebar.ctaLabel === 'string' ? sidebar.ctaLabel : undefined,
          ctaUrl: typeof sidebar.ctaUrl === 'string' ? sidebar.ctaUrl : undefined,
        }
      : undefined,
    footer: footer
      ? {
          html: typeof footer.html === 'string' ? footer.html : undefined,
          signature: typeof footer.signature === 'string' ? footer.signature : undefined,
        }
      : undefined,
  };
};

export const GcsUploadPage: FC<GcsUploadPageProps> = ({ block, lessonId: _lessonId }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const xhrRef = useRef<XMLHttpRequest | null>(null);
  const [progress, setProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const startUpload = useStartGcsUpload();
  const notifyUpload = useNotifyGcsUpload();
  const completeBlock = useCompleteBlock();
  const getDownloadUrl = useGetGcsDownloadUrl();
  const { data: uploads = [], isLoading: isLoadingUploads } = useGetMyGcsUploadsByBlock(block.id);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const allowed = parseAllowedMediaTypes(block.metadata);
  const acceptAttr =
    allowed.length > 0
      ? allowed
          .flatMap(k => [
            ...GCS_MEDIA_TYPE_PRESETS[k].extensions.map(e => `.${e}`),
            ...GCS_MEDIA_TYPE_PRESETS[k].mimeTypes,
          ])
          .join(',')
      : undefined;
  const allowedLabel =
    allowed.length > 0 ? allowed.map(k => GCS_MEDIA_TYPE_PRESETS[k].label).join(', ') : 'Any file type';

  const handleDownload = async (uploadId: string) => {
    if (downloadingId) {
      return;
    }
    setDownloadingId(uploadId);
    try {
      const { url } = await getDownloadUrl.mutateAsync(uploadId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('GCS download URL fetch failed:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch download URL.');
    } finally {
      setDownloadingId(null);
    }
  };

  const handlePick = () => inputRef.current?.click();

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) {
      return;
    }
    const file = files[0];
    setError(null);
    setSuccessMessage(null);
    try {
      setIsUploading(true);
      setProgress(0);
      const fileExtension = file.name.includes('.') ? file.name.split('.').pop()! : '';
      if (!isGcsFileAllowed(allowed, file.type, fileExtension)) {
        setError(`Only ${allowedLabel} files are allowed for this upload.`);
        setIsUploading(false);
        return;
      }
      const started = await startUpload.mutateAsync({
        blockId: block.id,
        data: {
          fileExtension,
          contentType: file.type || 'application/octet-stream',
          fileSize: file.size,
        },
      });
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhrRef.current = xhr;
        xhr.open('PUT', started.uploadUrl, true);
        if (file.type) {
          xhr.setRequestHeader('Content-Type', file.type);
        }
        xhr.upload.onprogress = e => {
          if (e.lengthComputable) {
            setProgress(Math.round((e.loaded / e.total) * 100));
          }
        };
        xhr.onload = () =>
          xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed (${xhr.status})`));
        xhr.onerror = () => reject(new Error('Network error during upload'));
        xhr.onabort = () => reject(new Error(UPLOAD_CANCELLED));
        xhr.send(file);
      });
      await notifyUpload.mutateAsync({ uploadId: started.uploadId, blockId: block.id });
      // Upload done → complete this block (backend verifies the upload row).
      completeBlock.mutate(block.id);
      setSuccessMessage(block.gcsSuccessMessage || 'Upload complete!');
    } catch (error) {
      if (error instanceof Error && error.message === UPLOAD_CANCELLED) {
        // User cancelled the upload — silently return to the idle state.
        setError(null);
      } else {
        console.error('GCS upload failed:', error);
        setError(error instanceof Error ? error.message : 'Upload failed. Please try again.');
      }
    } finally {
      setIsUploading(false);
      setProgress(0);
      xhrRef.current = null;
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    }
  };

  const handleCancel = () => {
    xhrRef.current?.abort();
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!isUploading) {
      setIsDragOver(true);
    }
  };
  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  };
  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    if (isUploading) {
      return;
    }
    handleFiles(e.dataTransfer.files);
  };

  const sanitizedBody = block.body ? DOMPurify.sanitize(block.body) : '';
  const layout = parseLayout(block.metadata);
  const sidebarHtml = layout.sidebar?.html ? DOMPurify.sanitize(layout.sidebar.html) : '';
  const footerHtml = layout.footer?.html ? DOMPurify.sanitize(layout.footer.html) : '';
  const showSidebar = !!(layout.sidebar?.html || layout.sidebar?.title);

  const mainColumn = (
    <Box>
      <Box
        component="h1"
        sx={{
          ...SERIF_FONT_OVERRIDE,
          fontWeight: 700,
          fontSize: { xs: '1.75rem', md: '2.125rem' },
          lineHeight: 1.2,
          color: '#1a1a1a',
          m: 0,
          mb: 2,
        }}
      >
        {block.title || 'Upload'}
      </Box>

      {sanitizedBody && (
        <Box
          sx={{
            mb: 2.5,
            color: '#3a3a3a',
            lineHeight: 1.6,
            '& p': { m: 0, mb: 1 },
            '& p:last-child': { mb: 0 },
            '& a': { color: '#ff4d6d' },
          }}
          dangerouslySetInnerHTML={{ __html: sanitizedBody }}
        />
      )}

      {layout.deadlineLabel && (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            bgcolor: '#ffe1e7',
            color: '#ff4d6d',
            fontWeight: 600,
            fontSize: '0.9rem',
            px: 1.75,
            py: 0.75,
            borderRadius: '999px',
            mb: 3,
          }}
        >
          <MdAccessTime size={16} />
          {layout.deadlineLabel}
        </Box>
      )}

      <input
        ref={inputRef}
        type="file"
        hidden
        accept={acceptAttr}
        onChange={e => handleFiles(e.target.files)}
        disabled={isUploading}
      />

      <Box
        onClick={() => !isUploading && handlePick()}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          border: '2px dashed',
          borderColor: isUploading ? 'rgba(0,0,0,0.12)' : isDragOver ? '#ff4d6d' : '#ffb6c4',
          bgcolor: isDragOver ? '#fff0f3' : '#ffffff',
          borderRadius: 3,
          py: 5,
          px: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1.5,
          cursor: isUploading ? 'default' : 'pointer',
          transition: 'all 0.15s',
          '&:hover': isUploading ? {} : { borderColor: '#ff4d6d', bgcolor: '#fff8f9' },
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            bgcolor: '#ffe1e7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {isUploading ? (
            <CircularProgress size={28} sx={{ color: '#ff4d6d' }} />
          ) : (
            <MdCloudUpload size={28} color="#ff4d6d" />
          )}
        </Box>
        <Typography sx={{ fontWeight: 700, color: '#1a1a1a', fontSize: '1.05rem' }}>
          {isUploading ? `Uploading… ${progress}%` : 'Drag & drop your video here'}
        </Typography>
        {!isUploading && (
          <Typography sx={{ color: '#3a3a3a', fontSize: '0.95rem' }}>
            or{' '}
            <Box
              component="span"
              onClick={e => {
                e.stopPropagation();
                handlePick();
              }}
              sx={{
                color: '#ff4d6d',
                textDecoration: 'underline',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              browse files
            </Box>{' '}
            to upload
          </Typography>
        )}
        <Typography variant="caption" sx={{ color: '#7a7a7a', letterSpacing: 0.3 }}>
          {allowedLabel} · Max 500MB
        </Typography>
        {isUploading && (
          <Box sx={{ width: '100%', mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{ width: '100%', '& .MuiLinearProgress-bar': { bgcolor: '#ff4d6d' } }}
            />
            <Box
              component="button"
              type="button"
              onClick={e => {
                e.stopPropagation();
                handleCancel();
              }}
              sx={{
                mt: 1.5,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                color: '#ff4d6d',
                fontWeight: 600,
                fontSize: '0.9rem',
                background: 'none',
                border: '1px solid #ffb6c4',
                borderRadius: '999px',
                px: 2,
                py: 0.75,
                cursor: 'pointer',
                transition: 'all 0.15s',
                '&:hover': { bgcolor: '#fff0f3', borderColor: '#ff4d6d' },
              }}
            >
              <MdClose size={16} />
              Cancel upload
            </Box>
          </Box>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mt: 2 }}>
          {successMessage}
        </Alert>
      )}

      <Box sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#1a1a1a' }}>
            Your Uploaded Video(s)
          </Typography>
          {uploads.length > 0 && (
            <Box
              sx={{
                bgcolor: '#ffe1e7',
                color: '#ff4d6d',
                fontWeight: 700,
                fontSize: '0.8rem',
                px: 1,
                py: 0.25,
                borderRadius: '999px',
                minWidth: 22,
                textAlign: 'center',
              }}
            >
              {uploads.length}
            </Box>
          )}
        </Box>

        {isLoadingUploads ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={28} />
          </Box>
        ) : uploads.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            You have not uploaded anything yet.
          </Typography>
        ) : (
          <Box
            sx={{
              border: '1px solid rgba(0,0,0,0.08)',
              borderRadius: 2,
              overflow: 'hidden',
              bgcolor: 'white',
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr 100px', md: '1fr 160px 130px' },
                bgcolor: '#fafafa',
                px: { xs: 1, sm: 2 },
                py: 1.25,
                borderBottom: '1px solid rgba(0,0,0,0.08)',
                fontSize: '0.75rem',
                fontWeight: 700,
                letterSpacing: 0.6,
                color: '#7a7a7a',
                textTransform: 'uppercase',
              }}
            >
              <Box>Video Name</Box>
              <Box>Uploaded</Box>
              <Box />
            </Box>
            {uploads.map((row: TGcsUploadRow, idx: number) => {
              const isLast = idx === uploads.length - 1;
              const isDownloading = downloadingId === row.id;
              return (
                <Box
                  key={row.id}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr 100px', md: '1fr 160px 130px' },
                    alignItems: 'center',
                    px: { xs: 1, md: 2 },
                    py: 1.75,
                    gap: { xs: 1, md: 2 },
                    borderBottom: isLast ? 'none' : '1px solid rgba(0,0,0,0.06)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                    <Box
                      sx={{
                        width: { xs: 24, md: 30 },
                        height: { xs: 24, md: 30 },
                        borderRadius: 1,
                        bgcolor: '#fff0f3',
                        color: '#ff4d6d',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <FileIcon name={row.fileName} />
                    </Box>
                    <Typography
                      sx={{
                        fontSize: { xs: '0.8rem', md: '0.9rem' },
                        fontWeight: 500,
                        color: '#1a1a1a',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={row.fileName}
                    >
                      {row.fileName}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      display: { xs: 'none', md: 'block' },
                      fontSize: { xs: '0.7rem', md: '0.8rem' },
                      color: '#5a5a5a',
                    }}
                  >
                    {formatRowDate(row.createdAt)}
                  </Typography>
                  <Box>
                    <Box
                      component="button"
                      type="button"
                      disabled={isDownloading}
                      onClick={() => handleDownload(row.id)}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.75,
                        color: '#ff4d6d',
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        textDecoration: 'none',
                        background: 'none',
                        border: 'none',
                        p: 0,
                        cursor: isDownloading ? 'wait' : 'pointer',
                        '&:hover': { color: '#e63956', textDecoration: 'underline' },
                        '&:disabled': { opacity: 0.6, cursor: 'wait' },
                      }}
                    >
                      {isDownloading ? (
                        <CircularProgress size={14} sx={{ color: '#ff4d6d' }} />
                      ) : (
                        <MdCloudDownload size={16} />
                      )}
                      Download
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      {footerHtml && (
        <Box
          sx={{
            mt: 5,
            pt: 3,
            borderTop: '1px solid rgba(0,0,0,0.08)',
          }}
        >
          <Box
            sx={{
              bgcolor: '#e8f5ee',
              borderRadius: 2,
              p: 2.5,
              color: '#2c3e2f',
              lineHeight: 1.6,
              '& p': { m: 0, mb: 1 },
              '& p:last-child': { mb: 0 },
              '& a': { color: '#ff4d6d', fontWeight: 600 },
            }}
            dangerouslySetInnerHTML={{ __html: footerHtml }}
          />
          {layout.footer?.signature && (
            <Typography
              sx={{
                mt: 2.5,
                color: '#3a3a3a',
                fontStyle: 'italic',
                fontSize: '0.95rem',
              }}
            >
              {layout.footer.signature}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );

  const sidebar = showSidebar ? (
    <Box
      sx={{
        bgcolor: 'white',
        border: '1px solid rgba(0,0,0,0.08)',
        borderRadius: 2,
        p: 2.5,
      }}
    >
      {layout.sidebar?.title && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <MdVideoSettings size={20} color="#ff4d6d" />
          <Typography sx={{ fontWeight: 700, color: '#ff4d6d', fontSize: '1rem' }}>{layout.sidebar.title}</Typography>
        </Box>
      )}
      {sidebarHtml && (
        <Box
          sx={{
            color: '#3a3a3a',
            fontSize: '0.92rem',
            lineHeight: 1.55,
            '& ul, & ol': { pl: 2.5, m: 0, mb: 1 },
            '& li': { mb: 0.75 },
            '& p': { m: 0, mb: 1 },
            '& p:last-child': { mb: 0 },
            '& b, & strong': { fontWeight: 700 },
          }}
          dangerouslySetInnerHTML={{ __html: sidebarHtml }}
        />
      )}
      {layout.sidebar?.ctaUrl && layout.sidebar?.ctaLabel && (
        <Box
          component="a"
          href={layout.sidebar.ctaUrl}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            mt: 2,
            px: 2,
            py: 1,
            border: '1px solid #ff4d6d',
            color: '#ff4d6d',
            borderRadius: '999px',
            fontWeight: 600,
            fontSize: '0.9rem',
            textDecoration: 'none',
            '&:hover': { bgcolor: '#fff0f3' },
          }}
        >
          <MdPlayArrow size={18} />
          {layout.sidebar.ctaLabel}
        </Box>
      )}
    </Box>
  ) : null;

  return (
    <Box>
      <Box
        sx={{
          display: 'grid',
          gap: { xs: 3, md: 4 },
          gridTemplateColumns: showSidebar ? { xs: '1fr', md: '1fr 280px' } : '1fr',
          maxWidth: showSidebar ? 1200 : 780,
          mx: 'auto',
        }}
      >
        {mainColumn}
        {sidebar && <Box>{sidebar}</Box>}
      </Box>
    </Box>
  );
};
